import React, { useEffect, useRef, useState } from "react";

/**
 * Spreedly Hosted Fields Tokenizer — E2E test surface.
 *
 * This page exists to capture a real Spreedly payment_method.token in the E2E
 * sandbox. Movmo's Spreedly account has iframe-only tokenization with
 * Enhanced Security enabled, which means the only valid card-collection path
 * is Spreedly's Hosted Fields v2 SDK initialised with a server-signed nonce.
 *
 * Flow:
 *   1. Paste a Movmo OAuth access token (the `access_token` from
 *      /v1/oauth/token, scopes payments.read payments.write) — see "Auth".
 *   2. Click "Fetch session" — calls GET /v1/payments/tokenization-session.
 *   3. The iframes mount; fill in test card details (4111 1111 1111 1111).
 *   4. Click "Tokenize" — Spreedly returns a payment_method.token.
 *   5. Use the printed curls to:
 *        a. Retain the token (move it from "cached" → "retained" in the vault).
 *        b. Add it to a Movmo user via POST /v1/users/{id}/payment-methods/from-token.
 *
 * Auth:
 *   The /v1/payments/tokenization-session endpoint requires a real user JWT.
 *   Easiest way to get one in dev:
 *     - Use the @movmo/connect-button OAuth flow at /connect-button with
 *       scopes payments.read and payments.write. After successful sign-in,
 *       the callback page logs the access token to the console (and could
 *       stash it in sessionStorage). Copy that value.
 *     - The browser session cookie (movmo_session_token) is NOT accepted as
 *       a Bearer token; the API rejects first-party session tokens sent
 *       that way.
 *
 *   Note: e2e.api.movmo.io's CORS allowlist must include this app's origin
 *   (http://localhost:3000 by default) for fetch to work. If you see a CORS
 *   error, fall back to the curl-based flow described inline below.
 *
 * Test cards (Spreedly Test gateway always succeeds for these):
 *   4111 1111 1111 1111  — Visa
 *   5555 5555 5555 4444  — Mastercard
 *   3782 8224 6310 005   — Amex
 *   exp 12 / 2030, CVV 123, ZIP 94105
 *
 * Spreedly Hosted Fields v2 docs: https://docs.spreedly.com/reference/iframe/v1/
 */

// Loose typing for the global Spreedly object loaded via <script>.
//
// Enhanced Security note: the certificateToken/nonce/timestamp/signature
// fields go in the init() opts (camelCase), NOT in tokenizeCreditCard().
// Confirmed by inspecting iframe-v1.min.js — the SDK reads them off the init
// options object once and stores them on the Spreedly instance for the
// lifetime of the session.
declare global {
    interface Window {
        Spreedly?: {
            init: (
                envKey: string,
                opts: {
                    numberEl: string;
                    cvvEl: string;
                    // Enhanced Security signed-session fields:
                    certificateToken?: string;
                    nonce?: string;
                    timestamp?: number;
                    signature?: string;
                },
            ) => void;
            on: (event: string, cb: (...args: unknown[]) => void) => void;
            removeHandlers: () => void;
            tokenizeCreditCard: (opts: Record<string, unknown>) => void;
            setStyle: (field: "number" | "cvv", style: string) => void;
            setPlaceholder: (field: "number" | "cvv", text: string) => void;
            setNumberFormat: (
                format: "prettyFormat" | "maskedFormat" | "plainFormat",
            ) => void;
            validate: () => void;
        };
    }
}

const SPREEDLY_SDK_URL = "https://core.spreedly.com/iframe/iframe-v1.min.js";
const API_BASE_URL =
    process.env.REACT_APP_MOVMO_API_URL || "https://e2e.api.movmo.io";
// API Gateway in front of monolith-api requires X-Api-Key on every request,
// even on routes that also enforce JWT (RBAC). Without this header, the
// gateway returns 403 before the request reaches the route handler.
const API_KEY = process.env.REACT_APP_MOVMO_API_KEY || "";

interface TokenizationSession {
    environmentKey: string;
    certificateToken: string;
    nonce: string;
    timestamp: number;
    signature: string;
}

// Spreedly Test gateway accepts these. Source:
// https://docs.spreedly.com/reference/test-card-numbers/
//
// The PAN and CVV cannot be programmatically populated into the Hosted Fields
// iframes (security feature — the parent page never has DOM access to those
// inputs). The dropdown only auto-fills the cardholder fields rendered by
// our parent page; we surface the PAN and CVV as hints the user must type
// into the iframes by hand.
interface TestCard {
    label: string;
    pan: string;
    cvv: string;
    firstName: string;
    lastName: string;
    month: string;
    year: string;
    zip: string;
    /** Expected outcome when authorising against the Spreedly Test gateway. */
    outcome: "approved" | "declined" | "invalid";
    note?: string;
}

const TEST_CARDS: TestCard[] = [
    {
        label: "Visa — Approved",
        pan: "4111 1111 1111 1111",
        cvv: "123",
        firstName: "Approved",
        lastName: "Visa",
        month: "12",
        year: "2030",
        zip: "94105",
        outcome: "approved",
    },
    {
        label: "Mastercard — Approved",
        pan: "5555 5555 5555 4444",
        cvv: "123",
        firstName: "Approved",
        lastName: "Mastercard",
        month: "12",
        year: "2030",
        zip: "94105",
        outcome: "approved",
    },
    {
        label: "American Express — Approved",
        pan: "3782 822463 10005",
        cvv: "1234",
        firstName: "Approved",
        lastName: "Amex",
        month: "12",
        year: "2030",
        zip: "94105",
        outcome: "approved",
        note: "Amex CVV is 4 digits.",
    },
    {
        label: "Discover — Approved",
        pan: "6011 1111 1111 1117",
        cvv: "123",
        firstName: "Approved",
        lastName: "Discover",
        month: "12",
        year: "2030",
        zip: "94105",
        outcome: "approved",
    },
    {
        label: "JCB — Approved",
        pan: "3530 1113 3330 0000",
        cvv: "123",
        firstName: "Approved",
        lastName: "JCB",
        month: "12",
        year: "2030",
        zip: "94105",
        outcome: "approved",
    },
    {
        label: "Visa — Declined (gateway)",
        pan: "4012 8888 8888 1881",
        cvv: "123",
        firstName: "Declined",
        lastName: "Visa",
        month: "12",
        year: "2030",
        zip: "94105",
        outcome: "declined",
        note: "Tokenises successfully; declines at AuthorizeTransaction.",
    },
];

interface SpreedlyPaymentMethod {
    token: string;
    last_four_digits: string;
    card_type: string;
    month: number;
    year: number;
    first_name: string;
    last_name: string;
    storage_state: string;
    full_name: string;
    [key: string]: unknown;
}

const CARD: React.CSSProperties = {
    maxWidth: 760,
    margin: "40px auto",
    background: "#fff",
    border: "1px solid #e7e5e4",
    borderRadius: 12,
    padding: 32,
    boxShadow: "0 1px 3px rgba(0,0,0,0.04)",
    fontFamily: "-apple-system, BlinkMacSystemFont, 'Inter', sans-serif",
};

const SECTION: React.CSSProperties = {
    marginBottom: 24,
    paddingBottom: 24,
    borderBottom: "1px solid #f5f5f4",
};

const SECTION_LAST: React.CSSProperties = { ...SECTION, borderBottom: "none" };

const LABEL: React.CSSProperties = {
    display: "block",
    fontSize: 12,
    fontWeight: 600,
    color: "#1B2A4A",
    marginBottom: 6,
    textTransform: "uppercase",
    letterSpacing: 0.4,
};

const INPUT: React.CSSProperties = {
    width: "100%",
    padding: "10px 12px",
    fontSize: 14,
    border: "1px solid #d6d3d1",
    borderRadius: 6,
    fontFamily: "inherit",
    boxSizing: "border-box",
    // Explicit color: the connect-button package's bundled Tailwind preflight
    // resets inherited text color globally, so without this the typed text
    // renders white-on-white. Same workaround as ConnectButtonCallback.tsx.
    color: "#171412",
    background: "#fff",
};

const IFRAME_MOUNT: React.CSSProperties = {
    ...INPUT,
    height: 40,
    padding: 0,
    overflow: "hidden",
    background: "#fff",
};

const BUTTON: React.CSSProperties = {
    background: "#FF4D6D",
    color: "#fff",
    border: "none",
    borderRadius: 6,
    padding: "10px 18px",
    fontSize: 14,
    fontWeight: 600,
    cursor: "pointer",
};

const BUTTON_DISABLED: React.CSSProperties = {
    ...BUTTON,
    background: "#d6d3d1",
    cursor: "not-allowed",
};

const PRE: React.CSSProperties = {
    background: "#fafaf9",
    border: "1px solid #e7e5e4",
    borderRadius: 6,
    padding: 12,
    fontSize: 12,
    color: "#171412",
    fontFamily:
        "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace",
    overflowX: "auto",
    whiteSpace: "pre-wrap",
    wordBreak: "break-all",
    margin: 0,
};

export default function SpreedlyTokenizer() {
    const [bearerToken, setBearerToken] = useState("");
    const [session, setSession] = useState<TokenizationSession | null>(null);
    const [sessionError, setSessionError] = useState<string | null>(null);
    const [sdkReady, setSdkReady] = useState(false);
    const [tokenizing, setTokenizing] = useState(false);
    const [paymentMethod, setPaymentMethod] =
        useState<SpreedlyPaymentMethod | null>(null);
    const [tokenizeErrors, setTokenizeErrors] = useState<unknown[]>([]);

    const [firstName, setFirstName] = useState("Test");
    const [lastName, setLastName] = useState("User");
    const [month, setMonth] = useState("12");
    const [year, setYear] = useState("2030");
    const [zip, setZip] = useState("94105");
    // Picked test card (drives the cardholder-field auto-fill + surfaces PAN/CVV hints).
    const [selectedCardIdx, setSelectedCardIdx] = useState<number>(0);

    const selectedCard = TEST_CARDS[selectedCardIdx];

    const applyTestCard = (idx: number) => {
        setSelectedCardIdx(idx);
        const card = TEST_CARDS[idx];
        setFirstName(card.firstName);
        setLastName(card.lastName);
        setMonth(card.month);
        setYear(card.year);
        setZip(card.zip);
    };

    // Guard against React 18 StrictMode double-invocation re-running Spreedly.init.
    // Track which session reference has been initialised. Comparing the ref
    // value against the session lets us (a) skip a duplicate init when
    // StrictMode runs the effect twice with the same session, and (b) re-init
    // automatically when the user clicks "Fetch session" again and gets a
    // fresh signed nonce. Plain boolean `initRef` couldn't tell those cases
    // apart.
    const initSessionRef = useRef<TokenizationSession | null>(null);

    // Load the Spreedly Hosted Fields SDK once.
    useEffect(() => {
        if (document.querySelector(`script[src="${SPREEDLY_SDK_URL}"]`)) {
            return;
        }
        const tag = document.createElement("script");
        tag.src = SPREEDLY_SDK_URL;
        tag.async = true;
        document.head.appendChild(tag);
    }, []);

    // Initialise Spreedly Hosted Fields once we have a session AND the SDK is loaded.
    useEffect(() => {
        if (!session) return;

        let cancelled = false;

        const tryInit = () => {
            // Guard inside tryInit so the StrictMode double-mount cleanup
            // doesn't allow two parallel init() calls to fight over iframe
            // registration. Compare against the session reference rather
            // than a boolean so a fresh "Fetch session" click correctly
            // re-inits with the new signed nonce.
            if (cancelled) return;
            if (initSessionRef.current === session) return;
            if (!window.Spreedly) {
                window.setTimeout(tryInit, 100);
                return;
            }
            initSessionRef.current = session;
            // If the SDK was already loaded with a previous session, our new
            // init() call will go through its built-in `isLoaded() && unload()`
            // path. Drop our cached event handlers so they don't double-fire
            // against the freshly-mounted iframes.
            window.Spreedly.removeHandlers();
            setSdkReady(false);
            // Sanity log: surface the actual values being handed to the SDK
            // so we can spot serialisation issues (e.g. timestamp NaN, blank
            // signature) without poking at the iframe internals.
            // eslint-disable-next-line no-console
            console.log("[Spreedly] init opts", {
                envKey: session.environmentKey,
                certificateToken: session.certificateToken?.slice(0, 12) + "…",
                nonce: session.nonce?.slice(0, 12) + "…",
                timestamp: session.timestamp,
                signatureLen: session.signature?.length,
            });
            window.Spreedly.init(session.environmentKey, {
                numberEl: "spreedly-number",
                cvvEl: "spreedly-cvv",
                // Enhanced Security: signed nonce read off init opts (camelCase).
                certificateToken: session.certificateToken,
                nonce: session.nonce,
                timestamp: session.timestamp,
                signature: session.signature,
            });

            window.Spreedly.on("ready", () => {
                // Explicit color is critical — without it the iframe text
                // renders invisibly under the connect-button package's
                // bundled Tailwind preflight reset, and the user appears
                // to be typing nothing into a field that silently accepts
                // partial / mistyped PANs (which then fails Luhn at submit).
                const fieldCSS =
                    "color: #171412; background: #fff; font-size: 14px; " +
                    "font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace; " +
                    "padding: 10px 12px;";
                window.Spreedly?.setStyle("number", fieldCSS);
                window.Spreedly?.setStyle("cvv", fieldCSS);
                window.Spreedly?.setPlaceholder(
                    "number",
                    "4111 1111 1111 1111",
                );
                window.Spreedly?.setPlaceholder("cvv", "CVV");
                window.Spreedly?.setNumberFormat("prettyFormat");
                setSdkReady(true);
            });

            window.Spreedly.on("paymentMethod", (...args: unknown[]) => {
                // Spreedly invokes paymentMethod callback as (token, paymentMethodData).
                const [, pmData] = args as [string, SpreedlyPaymentMethod];
                setPaymentMethod(pmData);
                setTokenizing(false);
            });

            window.Spreedly.on("errors", (...args: unknown[]) => {
                setTokenizeErrors(args);
                setTokenizing(false);
            });
        };

        tryInit();

        return () => {
            // Just abort any pending poll; we keep initSessionRef pointed at
            // the current session so the second StrictMode mount sees "this
            // session is already inited" and skips. A fresh fetch produces a
            // new session reference, which falls through and re-inits.
            cancelled = true;
        };
    }, [session]);

    const fetchSession = async () => {
        setSessionError(null);
        if (!bearerToken.trim()) {
            setSessionError("Bearer token is required.");
            return;
        }
        if (!API_KEY) {
            setSessionError(
                "REACT_APP_MOVMO_API_KEY is not set in the build env. Restart the dev server with `npm run start:testing` (or :e2e).",
            );
            return;
        }
        try {
            const resp = await fetch(
                `${API_BASE_URL}/v1/payments/tokenization-session`,
                {
                    method: "GET",
                    headers: {
                        Authorization: `Bearer ${bearerToken.trim()}`,
                        "X-Api-Key": API_KEY,
                    },
                    credentials: "include",
                },
            );
            if (!resp.ok) {
                const text = await resp.text();
                setSessionError(
                    `HTTP ${resp.status}: ${text || resp.statusText}`,
                );
                return;
            }
            const data = (await resp.json()) as TokenizationSession;
            setSession(data);
        } catch (e) {
            setSessionError(
                `Network error: ${(e as Error).message}. ` +
                    "If this is CORS, fall back to the curl flow below.",
            );
        }
    };

    const tokenize = () => {
        if (!session || !window.Spreedly) return;
        setTokenizeErrors([]);
        setPaymentMethod(null);
        setTokenizing(true);
        // The SDK accepts the Enhanced Security signed-session params at BOTH
        // init() and tokenizeCreditCard() (we verified this in iframe-v1.min.js:
        // both functions read e.nonce / e.timestamp / e.certificateToken /
        // e.signature off their opts arg, all camelCase). Passing them at
        // tokenize time too is a belt-and-suspenders hedge against any init-
        // path race where the values got dropped (e.g. unload() between two
        // StrictMode mounts).
        // The "paymentMethod" name is the success *event* — the SDK method
        // is tokenizeCreditCard.
        window.Spreedly.tokenizeCreditCard({
            first_name: firstName,
            last_name: lastName,
            month,
            year,
            zip,
            // Enhanced Security signed-session (camelCase, per SDK source):
            certificateToken: session.certificateToken,
            nonce: session.nonce,
            timestamp: session.timestamp,
            signature: session.signature,
        });
    };

    return (
        <div
            style={{
                minHeight: "100vh",
                background: "#f5f5f4",
                padding: "20px 16px",
            }}
        >
            <div style={CARD}>
                <h1
                    style={{
                        fontSize: 22,
                        margin: "0 0 8px",
                        color: "#1B2A4A",
                    }}
                >
                    Spreedly Hosted Fields Tokenizer
                </h1>
                <p
                    style={{
                        color: "#57534E",
                        fontSize: 13,
                        marginTop: 0,
                        marginBottom: 24,
                    }}
                >
                    Captures a Spreedly <code>payment_method.token</code> via
                    the Hosted Fields v2 SDK. Use this to bootstrap an E2E
                    sandbox token for <code>setup_payment_spreedly.go</code> or
                    for ad-hoc Spreedly-vaulted booking tests.
                </p>

                {/* Step 1: Auth */}
                <section style={SECTION}>
                    <h2
                        style={{ fontSize: 14, color: "#1B2A4A", marginTop: 0 }}
                    >
                        1. Paste an OAuth access token
                    </h2>
                    <p style={{ color: "#57534E", fontSize: 12, marginTop: 0 }}>
                        The OAuth <code>access_token</code> from the{" "}
                        <code>@movmo/connect-button</code> callback (see{" "}
                        <code>/connect-button</code>), requested with scopes{" "}
                        <code>payments.read</code> and{" "}
                        <code>payments.write</code>.
                    </p>
                    <textarea
                        style={{
                            ...INPUT,
                            fontFamily: "ui-monospace, monospace",
                            fontSize: 12,
                            minHeight: 80,
                        }}
                        placeholder="eyJraWQiOiI..."
                        value={bearerToken}
                        onChange={(e) => setBearerToken(e.target.value)}
                    />
                    <button
                        style={{ ...BUTTON, marginTop: 12 }}
                        onClick={fetchSession}
                        disabled={!bearerToken.trim()}
                    >
                        Fetch session
                    </button>
                    {sessionError && (
                        <p
                            style={{
                                color: "#b91c1c",
                                fontSize: 12,
                                marginTop: 12,
                            }}
                        >
                            {sessionError}
                        </p>
                    )}
                    {session && (
                        <details style={{ marginTop: 12 }}>
                            <summary
                                style={{
                                    cursor: "pointer",
                                    fontSize: 12,
                                    color: "#57534E",
                                }}
                            >
                                Session loaded (
                                {session.environmentKey.slice(0, 12)}…)
                            </summary>
                            <pre style={{ ...PRE, marginTop: 8 }}>
                                {JSON.stringify(session, null, 2)}
                            </pre>
                        </details>
                    )}
                </section>

                {/* Step 2: Hosted Fields */}
                <section style={SECTION}>
                    <h2
                        style={{ fontSize: 14, color: "#1B2A4A", marginTop: 0 }}
                    >
                        2. Card details
                    </h2>
                    {!session && (
                        <p
                            style={{
                                color: "#57534E",
                                fontSize: 12,
                                fontStyle: "italic",
                            }}
                        >
                            Iframes mount once a session is loaded.
                        </p>
                    )}

                    {/* Test card dropdown: auto-fills cardholder fields and
                        surfaces the PAN/CVV the user must type into the
                        Hosted Fields iframes (the iframes can't be
                        programmatically populated). */}
                    <div style={{ marginBottom: 12 }}>
                        <label style={LABEL}>Test card preset</label>
                        <select
                            style={INPUT}
                            value={selectedCardIdx}
                            onChange={(e) =>
                                applyTestCard(parseInt(e.target.value, 10))
                            }
                        >
                            {TEST_CARDS.map((c, i) => (
                                <option key={i} value={i}>
                                    {c.label}
                                </option>
                            ))}
                        </select>
                        <div
                            style={{
                                marginTop: 8,
                                background: "#fef3c7",
                                border: "1px solid #fcd34d",
                                borderRadius: 6,
                                padding: 10,
                                fontSize: 12,
                                color: "#92400e",
                            }}
                        >
                            <strong>Type into the iframes:</strong> PAN{" "}
                            <code>{selectedCard.pan}</code>, CVV{" "}
                            <code>{selectedCard.cvv}</code>.
                            {selectedCard.note && (
                                <>
                                    {" "}
                                    <em>{selectedCard.note}</em>
                                </>
                            )}
                        </div>
                    </div>

                    <div
                        style={{
                            display: "grid",
                            gridTemplateColumns: "1fr 1fr",
                            gap: 12,
                        }}
                    >
                        <div>
                            <label style={LABEL}>First name</label>
                            <input
                                style={INPUT}
                                value={firstName}
                                onChange={(e) => setFirstName(e.target.value)}
                            />
                        </div>
                        <div>
                            <label style={LABEL}>Last name</label>
                            <input
                                style={INPUT}
                                value={lastName}
                                onChange={(e) => setLastName(e.target.value)}
                            />
                        </div>
                    </div>

                    <div style={{ marginTop: 12 }}>
                        <label style={LABEL}>
                            Card number — type <code>{selectedCard.pan}</code>{" "}
                            into the iframe below
                        </label>
                        <div id="spreedly-number" style={IFRAME_MOUNT} />
                    </div>

                    <div
                        style={{
                            display: "grid",
                            gridTemplateColumns: "1fr 1fr 1fr 1fr",
                            gap: 12,
                            marginTop: 12,
                        }}
                    >
                        <div>
                            <label style={LABEL}>Exp month</label>
                            <input
                                style={INPUT}
                                value={month}
                                onChange={(e) => setMonth(e.target.value)}
                                placeholder="12"
                            />
                        </div>
                        <div>
                            <label style={LABEL}>Exp year</label>
                            <input
                                style={INPUT}
                                value={year}
                                onChange={(e) => setYear(e.target.value)}
                                placeholder="2030"
                            />
                        </div>
                        <div>
                            <label style={LABEL}>
                                CVV ({selectedCard.cvv})
                            </label>
                            <div id="spreedly-cvv" style={IFRAME_MOUNT} />
                        </div>
                        <div>
                            <label style={LABEL}>ZIP</label>
                            <input
                                style={INPUT}
                                value={zip}
                                onChange={(e) => setZip(e.target.value)}
                                placeholder="94105"
                            />
                        </div>
                    </div>

                    <button
                        style={{
                            ...(sdkReady && !tokenizing
                                ? BUTTON
                                : BUTTON_DISABLED),
                            marginTop: 16,
                        }}
                        onClick={tokenize}
                        disabled={!sdkReady || tokenizing}
                    >
                        {tokenizing ? "Tokenizing…" : "Tokenize"}
                    </button>
                    {!sdkReady && session && (
                        <p
                            style={{
                                color: "#57534E",
                                fontSize: 12,
                                marginTop: 8,
                            }}
                        >
                            Waiting for Spreedly SDK to load…
                        </p>
                    )}
                    {tokenizeErrors.length > 0 && (
                        <pre
                            style={{ ...PRE, color: "#b91c1c", marginTop: 12 }}
                        >
                            {JSON.stringify(tokenizeErrors, null, 2)}
                        </pre>
                    )}
                </section>

                {/* Step 3: Result */}
                <section style={SECTION_LAST}>
                    <h2
                        style={{ fontSize: 14, color: "#1B2A4A", marginTop: 0 }}
                    >
                        3. Result
                    </h2>
                    {!paymentMethod && (
                        <p
                            style={{
                                color: "#57534E",
                                fontSize: 12,
                                fontStyle: "italic",
                            }}
                        >
                            The token will appear here once tokenization
                            succeeds.
                        </p>
                    )}
                    {paymentMethod && (
                        <>
                            <div
                                style={{
                                    background: "#ecfdf5",
                                    border: "1px solid #6ee7b7",
                                    borderRadius: 6,
                                    padding: 12,
                                    marginBottom: 16,
                                }}
                            >
                                <div
                                    style={{
                                        fontSize: 12,
                                        color: "#1B2A4A",
                                        fontWeight: 600,
                                    }}
                                >
                                    payment_method.token
                                </div>
                                <code
                                    style={{
                                        fontSize: 14,
                                        color: "#171412",
                                        wordBreak: "break-all",
                                    }}
                                >
                                    {paymentMethod.token}
                                </code>
                                <div
                                    style={{
                                        fontSize: 12,
                                        color: "#57534E",
                                        marginTop: 8,
                                    }}
                                >
                                    {paymentMethod.card_type} ••••{" "}
                                    {paymentMethod.last_four_digits} —{" "}
                                    storage_state={paymentMethod.storage_state}
                                </div>
                            </div>

                            <p
                                style={{
                                    fontSize: 12,
                                    color: "#1B2A4A",
                                    fontWeight: 600,
                                    marginBottom: 4,
                                }}
                            >
                                Retain (cached → retained, ~15-min TTL
                                otherwise):
                            </p>
                            <pre style={PRE}>
                                {retainCurl(paymentMethod.token)}
                            </pre>

                            <p
                                style={{
                                    fontSize: 12,
                                    color: "#1B2A4A",
                                    fontWeight: 600,
                                    marginBottom: 4,
                                    marginTop: 16,
                                }}
                            >
                                Add to your Movmo user (replace {"{userId}"}{" "}
                                with your user ID):
                            </p>
                            <pre style={PRE}>
                                {addToUserCurl(
                                    paymentMethod,
                                    bearerToken.trim(),
                                )}
                            </pre>

                            <details style={{ marginTop: 16 }}>
                                <summary
                                    style={{
                                        cursor: "pointer",
                                        fontSize: 12,
                                        color: "#57534E",
                                    }}
                                >
                                    Full paymentMethod payload
                                </summary>
                                <pre style={{ ...PRE, marginTop: 8 }}>
                                    {JSON.stringify(paymentMethod, null, 2)}
                                </pre>
                            </details>
                        </>
                    )}
                </section>
            </div>
        </div>
    );
}

// retainCurl uses the Spreedly access secret from environment — replace with
// your real env_key:access_secret pair when copy-pasting.
function retainCurl(token: string): string {
    return [
        "# Replace SPREEDLY_ENV_KEY and SPREEDLY_ACCESS_SECRET with the e2e values",
        "# (from AWS Secrets Manager: movmo_api/e2e/secrets).",
        `curl -X PUT \\`,
        `  -u "$SPREEDLY_ENV_KEY:$SPREEDLY_ACCESS_SECRET" \\`,
        `  -H "Content-Type: application/json" \\`,
        `  https://core.spreedly.com/v1/payment_methods/${token}/retain.json`,
    ].join("\n");
}

function addToUserCurl(pm: SpreedlyPaymentMethod, bearer: string): string {
    // The /from-token route is vault-neutral and derives last4 / brand /
    // expiration server-side from the Spreedly vault — clients only forward
    // the opaque token + a default flag. Field names match
    // monolith-api/routes/user_payments.go::createPaymentMethodFromTokenRequest.
    const body = {
        tokenizedPaymentMethodId: pm.token,
        isDefault: true,
    };
    return [
        `curl -X POST \\`,
        `  -H "Authorization: Bearer ${bearer || "<oauth-access-token>"}" \\`,
        `  -H "X-Api-Key: ${API_KEY || "<api-key>"}" \\`,
        `  -H "Content-Type: application/json" \\`,
        `  -d '${JSON.stringify(body)}' \\`,
        `  ${API_BASE_URL}/v1/users/{userId}/payment-methods/from-token`,
    ].join("\n");
}
